/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';
import { get } from 'js/lib/user';

import EnterpriseSkillsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseSkillsContext';
import LearnersLatestSkillProgressQuery from 'bundles/page-config-common/queries/LearnersLatestSkillProgress.graphql';
import QueryIfLearnerHasSkillProgress from 'bundles/page-config-common/queries/QueryIfLearnerHasSkillProgress.graphql';
import QueryProgressForTrendingSkillsInProgram from 'bundles/page-config-common/queries/QueryProgressForTrendingSkillsInProgram.graphql';
import type {
  LearnersLatestSkillProgressQuery as LearnersLatestSkillProgressQueryData,
  LearnersLatestSkillProgressQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/LearnersLatestSkillProgress';
import type {
  QueryIfLearnerHasSkillProgressQuery as QueryIfLearnerHasSkillProgressQueryData,
  QueryIfLearnerHasSkillProgressQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/QueryIfLearnerHasSkillProgress';
import type {
  QueryProgressForTrendingSkillsInProgramQuery as QueryProgressForTrendingSkillsInProgramQueryData,
  QueryProgressForTrendingSkillsInProgramQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/QueryProgressForTrendingSkillsInProgram';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { QUERY_PARAM_VALUES } from 'bundles/unified-home-common/constants/MyLearningConstants';
import { useCurrentMyLearningTab } from 'bundles/unified-home-common/contexts/MyLearningTabContext';
import { usePageSize } from 'bundles/unified-home-common/contexts/PageSizeContext';
import { getSkillProgressSortOption } from 'bundles/unified-home-common/utils/SkillDashboardUtils';

/**
 * @description This provider is used to fetch the skills data for the enterprise skills dashboard
 */
const EnterpriseSkillsProvider = ({ skillLimit, children }: React.PropsWithChildren<{ skillLimit?: number }>) => {
  const program = useProgram();
  const programId = program?.id;
  const location = useLocation();
  const { currentTab } = useCurrentMyLearningTab();
  const { pageSize, currentPageNum } = usePageSize();

  const lastRoutePath = location.pathname.slice(location.pathname.lastIndexOf('/') + 1);

  const isMyLearningPage = lastRoutePath === 'my-learning';
  const isOnSkillsTab = currentTab === QUERY_PARAM_VALUES.SKILLS_DASHBOARD;

  const {
    timeRange: timerangeInDays,
    skillLevel: skillLevelFilter,
    includeOnlyCompletedLevelSets: includeOnlySkillsWithCompletedLevelsets,
    sortField = 'UPDATED_AT',
  } = location.query;

  const { loading: isHasSkillProgressDataLoading, data: hasSkillProgressData } = useQuery<
    QueryIfLearnerHasSkillProgressQueryData,
    QueryIfLearnerHasSkillProgressQueryVariables
  >(QueryIfLearnerHasSkillProgress, {
    variables: {
      // skip checks that the programId is defined
      // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
      programId: programId!!,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    skip: !programId || (isMyLearningPage && !isOnSkillsTab) || !get?.()?.id,
    errorPolicy: 'all',
    ssr: false,
  });

  const hasSkillProgress =
    !!hasSkillProgressData?.SkillsProgress?.queryLearnersLatestSkillProgress?.hasProgressInProgram;

  const {
    data: latestSkills,
    networkStatus: latestSkillsNetworkStatus,
    loading: areLatestSkillsLoading,
    error: latestSkillsProgressError,
  } = useQuery<LearnersLatestSkillProgressQueryData, LearnersLatestSkillProgressQueryVariables>(
    LearnersLatestSkillProgressQuery,
    {
      variables: {
        // skip checks that the programId is defined
        // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
        programId: programId!!,
        includeOnlySkillsWithCompletedLevelsets: includeOnlySkillsWithCompletedLevelsets === 'true',
        limit: skillLimit || pageSize,
        cursor: ((currentPageNum - 1) * pageSize).toString(),
        sortOption: getSkillProgressSortOption(sortField),
        timerangeInDays: parseInt(timerangeInDays, 10),
        skillLevelFilter,
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      // queryLearnersLatestSkillProgress will get the user id from request context, but only logged in learners have skills progress
      // If the sortField is RELEVANCY, than it should be searching instead
      skip: !programId || (isMyLearningPage && !isOnSkillsTab) || !get?.()?.id || sortField === 'RELEVANCY',
      errorPolicy: 'all',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    }
  );

  const latestSkillsWithProgress = latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgress?.elements ?? [];
  const totalLatestSkills =
    latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgress?.pagination?.totalElements ?? 0;

  const {
    data: trendingSkills,
    networkStatus: trendingSkillsStatus,
    error: trendingSkillsError,
  } = useQuery<QueryProgressForTrendingSkillsInProgramQueryData, QueryProgressForTrendingSkillsInProgramQueryVariables>(
    QueryProgressForTrendingSkillsInProgram,
    {
      variables: {
        // skip checks that the programId is defined
        // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
        programId: programId!!,
        limit: skillLimit || pageSize,
        cursor: ((currentPageNum - 1) * pageSize).toString(),
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      // queryLearnersLatestSkillProgress will get the user id from request context, but only logged in learners have skills progress
      skip:
        hasSkillProgress ||
        !programId ||
        (isMyLearningPage && !isOnSkillsTab) ||
        !get?.()?.id ||
        sortField === 'RELEVANCY',
      errorPolicy: 'all',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    }
  );

  // If there are no latest skills, fallback to trending skills
  const shouldUseLatestSkills = areLatestSkillsLoading || hasSkillProgress || isHasSkillProgressDataLoading;
  const networkStatus = shouldUseLatestSkills ? latestSkillsNetworkStatus : trendingSkillsStatus;
  const trendingSkillsWithProgress =
    trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInProgram?.elements ?? [];
  const totalTrendingSkils =
    trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInProgram?.pagination?.totalElements ?? 0;
  // If there is an error, hasSkillProgress will always be false so it incorrectly returns trending skills
  const hasError = Boolean(latestSkillsProgressError || trendingSkillsError);

  return (
    <EnterpriseSkillsContext.Provider
      value={{
        latestSkillsWithProgress: shouldUseLatestSkills ? latestSkillsWithProgress : trendingSkillsWithProgress,
        hasSkillProgress,
        // this loading state captures initial loading as well as refetching, taken from isNetworkRequestInFlight
        // (https://github.com/apollographql/apollo-client/blob/main/src/core/networkStatus.ts), which won't import correctly in snowpack or I would use it directly
        // 7 is their highest loading status
        areLatestSkillsLoading: networkStatus ? networkStatus < 7 : false,
        totalLatestSkills: shouldUseLatestSkills ? totalLatestSkills : totalTrendingSkils,
        hasError,
      }}
    >
      {children}
    </EnterpriseSkillsContext.Provider>
  );
};

export default EnterpriseSkillsProvider;
